/*
 * Sub-type Rule List Controller — powers the Sub-types tab on the
 * Calendar colors settings form. Handles:
 *   - adding a new rule from a <template> (assigns a fresh UUID) and
 *     dropping it into the right parent-event-type group
 *   - removing a rule (with confirm prompt); cleans up empty groups
 *   - reordering rules within a group (move up / move down)
 *   - toggling enabled/disabled (flips the eye icon and hidden field)
 *   - when the parent type changes on a rule, migrates the row into
 *     the matching group and creates/removes group containers as needed
 *   - toggling the expanded edit detail panel
 *   - hiding up/down arrows when a row is the only / first / last in
 *     its group so users don't wonder why a click does nothing
 */
import { Controller } from '@hotwired/stimulus';
import uuid from 'lib/uuid';

export default class extends Controller {
  static targets = ['list', 'template', 'row', 'detail', 'empty', 'enabledField', 'enabledIcon', 'moveUp', 'moveDown'];

  static values = {
    eventTypes: { type: Array, default: [] },
  };

  connect() {
    this.refreshMoveControls();
  }

  add(event) {
    event.preventDefault();
    if (!this.hasTemplateTarget) return;
    const fragment = this.templateTarget.content.cloneNode(true);
    const id = uuid();
    const row = fragment.querySelector('.cc-sub-type-row');
    if (!row) return;

    row.innerHTML = row.innerHTML.replace(/__NEW_ID__/g, id);
    row.id = `cc-rule-${id}`;
    row.dataset.tagId = id;

    const parentId = row.dataset.parentEventType;
    this.ensureGroup(parentId).appendChild(row);

    if (this.hasEmptyTarget) this.emptyTarget.hidden = true;

    const detail = row.querySelector('[data-sub-type-rule-list-target="detail"]');
    if (detail) detail.hidden = false;

    this.refreshMoveControls();
  }

  remove(event) {
    event.preventDefault();
    const row = event.currentTarget.closest('[data-sub-type-rule-list-target="row"]');
    if (!row) return;
    const { confirmMessage } = event.currentTarget.dataset;
    if (confirmMessage && !window.confirm(confirmMessage)) return;
    const group = row.parentElement;
    row.remove();
    this.cleanupEmptyGroup(group);
    this.refreshMoveControls();
  }

  moveUp(event) {
    event.preventDefault();
    const row = event.currentTarget.closest('[data-sub-type-rule-list-target="row"]');
    if (!row) return;
    const prev = this.previousRow(row);
    if (!prev) return;
    prev.parentNode.insertBefore(row, prev);
    this.refreshMoveControls();
  }

  moveDown(event) {
    event.preventDefault();
    const row = event.currentTarget.closest('[data-sub-type-rule-list-target="row"]');
    if (!row) return;
    const next = this.nextRow(row);
    if (!next) return;
    next.parentNode.insertBefore(next, row);
    this.refreshMoveControls();
  }

  toggleExpand(event) {
    event.preventDefault();
    const row = event.currentTarget.closest('[data-sub-type-rule-list-target="row"]');
    if (!row) return;
    const detail = row.querySelector('[data-sub-type-rule-list-target="detail"]');
    if (!detail) return;
    detail.hidden = !detail.hidden;
  }

  toggleEnabled(event) {
    event.preventDefault();
    const row = event.currentTarget.closest('[data-sub-type-rule-list-target="row"]');
    if (!row) return;
    const field = row.querySelector('[data-sub-type-rule-list-target="enabledField"]');
    const icon = row.querySelector('[data-sub-type-rule-list-target="enabledIcon"] i');
    if (!field) return;
    const nowEnabled = field.value !== 'true';
    field.value = nowEnabled ? 'true' : 'false';
    if (icon) {
      icon.classList.toggle('bi-eye', nowEnabled);
      icon.classList.toggle('bi-eye-slash', !nowEnabled);
    }
  }

  updateParent(event) {
    const row = event.currentTarget.closest('[data-sub-type-rule-list-target="row"]');
    if (!row) return;
    const value = event.detail?.value ?? event.currentTarget.value;
    if (!value) return;
    row.dataset.parentEventType = value;

    const currentGroup = row.parentElement;
    const targetGroup = this.ensureGroup(value);
    if (currentGroup !== targetGroup) {
      targetGroup.appendChild(row);
      this.cleanupEmptyGroup(currentGroup);
    }
    this.refreshMoveControls();
  }

  previousRow(row) {
    const rows = this.rowsInSameGroup(row);
    const idx = rows.indexOf(row);
    return idx > 0 ? rows[idx - 1] : null;
  }

  nextRow(row) {
    const rows = this.rowsInSameGroup(row);
    const idx = rows.indexOf(row);
    return idx >= 0 && idx < rows.length - 1 ? rows[idx + 1] : null;
  }

  rowsInSameGroup(row) {
    const group = row.parentElement;
    if (!group) return [row];
    return Array.from(group.querySelectorAll('[data-sub-type-rule-list-target="row"]'));
  }

  ensureGroup(parentId) {
    let group = this.listTarget.querySelector(`.cc-rule-group[data-parent-event-type="${parentId}"]`);
    if (group) return group;
    group = document.createElement('div');
    group.className = 'cc-rule-group mb-3';
    group.dataset.parentEventType = parentId;
    const header = document.createElement('div');
    header.className = 'text-muted text-uppercase small font-weight-bold mb-2';
    header.textContent = `Sub-types of ${this.parentName(parentId)}`;
    group.appendChild(header);
    this.listTarget.appendChild(group);
    return group;
  }

  cleanupEmptyGroup(group) {
    if (!group || !group.classList?.contains('cc-rule-group')) return;
    if (!group.querySelector('[data-sub-type-rule-list-target="row"]')) {
      group.remove();
    }
  }

  parentName(parentId) {
    const match = this.eventTypesValue.find((et) => et.id === parentId);
    return match ? match.name : '';
  }

  // Hides the up arrow on the first row of each group, the down arrow on
  // the last, and both arrows when a group only has a single rule — so
  // people don't click a button that can't do anything.
  refreshMoveControls() {
    const groups = this.listTarget.querySelectorAll('.cc-rule-group');
    groups.forEach((group) => {
      const rows = Array.from(group.querySelectorAll('[data-sub-type-rule-list-target="row"]'));
      rows.forEach((row, i) => {
        const upBtns = row.querySelectorAll('[data-sub-type-rule-list-target="moveUp"]');
        const downBtns = row.querySelectorAll('[data-sub-type-rule-list-target="moveDown"]');
        const hideUp = i === 0;
        const hideDown = i === rows.length - 1;
        upBtns.forEach((btn) => {
          btn.style.visibility = hideUp ? 'hidden' : '';
        });
        downBtns.forEach((btn) => {
          btn.style.visibility = hideDown ? 'hidden' : '';
        });
      });
    });
  }
}
